<template>
    <div class="company-panel-wrapper" v-if="blocked">
        <div class="company">
            <h2>This company is unavailable in trial mode, please select another</h2>
        </div>
    </div>
    <component-loader v-else-if="loading" />
    <div class="company-panel-wrapper" v-else>
        <title-bar class="space-between">
            <div class="left">
                <div>{{ props.selectedCompany }}</div>
                <primevue-button v-tooltip="exportTooltip" :disabled="!canExport" @click="exportCompanyCsv" text>
                    <font-awesome-icon :icon="['fas', 'file-csv']" />
                </primevue-button>
            </div>
            <select-button :options="filteredDataOptions" optionLabel="name" optionValue="value" v-model="lookAtAllData" @update:model-value="initialise" />
        </title-bar>
        <div class="company-panel-inner">
            <div class="projects-table" v-if="projectsInvolvedTableData.length">
                <basic-table
                    title="Projects Involved"
                    :headers="projectsInvolvedTableHeaders"
                    :data="projectsInvolvedTableData"
                    :unfiltered-headers="['Status']"
                    @select:row="openProjectDialog"
                    @update:filtered-data="updateFilteredData"
                />
            </div>
            <div class="company-map-container">
                <generic-map
                    :active-areas="filteredCountries"
                    :marker-array="projectsInvolvedMarkers"
                    :marker-colour="[226, 50, 126, 255]"
                    geojsonFeatureId="ISO2"
                    mapUrl="/data/europe.json"
                >
                    <template #mapTitle>
                        <div class="map-title">Company Portfolio</div>
                    </template>
                </generic-map>
            </div>
            <div class="partner-table" v-if="partnerTableData.length">
                <basic-table
                    title="Partners Involved"
                    :headers="partnerTableHeaders"
                    :data="partnerTableData"
                    :filter-headers="['Partner', 'Role']"
                    :cell-selection-headers="['Partner', 'Project Name']"
                    @select:cell="openPartnersDialog"
                />
            </div>
            <title-bar class="border-top center">
                <storage-tracking-selector
                    :options="BATTERY_TYPE_CHART_OPTIONS"
                    inline
                    :title="isMobile ? 'Data on:' : 'Show me data on:'"
                    v-model="selectedBatteryType"
                />
                <storage-tracking-selector
                    :options="CHART_SPLIT_OPTIONS"
                    inline
                    :title="isMobile ? 'by:' : 'Split by:'"
                    v-model="selectedChartSplit"
                />
            </title-bar>
            <template v-if="selectedChartSplit === 'year'">
                <base-stacked-bar-chart
                    class="chart"
                    :title="`${cleanChartTitle} by Year`"
                    x-axis-category="Year"
                    raw
                    :rawData="yearChartData"
                    :suffix="chartTooltipUnit"
                />
            </template>
            <template v-else>
                <base-horizontal-bar-chart
                    class="chart"
                    :rawData="countryTechOrStatusChartData"
                    :x-axis-category="selectedChartSplitName"
                    :title="`${cleanChartTitle} by ${selectedChartSplit}`"
                    :suffix="chartTooltipUnit"
                />
            </template>
        </div>
    </div>
    <div>
        <central-modal
            v-if="companyOpenInModal"
            v-model:visible="modalVisible"
            :header="companyOpenInModal"
            hide-header
            size="medium"
        >
            <company-panel
                :selected-company="companyOpenInModal"
                :lookAtAllDataProp="true"
            />
        </central-modal>
        <central-modal
            v-if="projectOpenInModal"
            :header="projectOpenInModal.projectName"
            v-model:visible="modalVisible"
            hide-header
            size="medium"
        >
            <project-panel
                :selectedProject="projectOpenInModal"
            />
        </central-modal>
    </div>
</template>

<script setup lang="ts">
import CentralModal from '@/components/common/CentralModal.vue';
import GenericMap from '@/components/map/GenericMap.vue';
import ComponentLoader from '@/components/layout/ComponentLoader.vue';
import PrimevueButton from 'primevue/button';
import SelectButton from 'primevue/selectbutton';
import { DataApi } from '@/api/storageTracking/DataApi.ts';
import { useAllDataStore } from '@/stores/storageTracking/AllDataStore.ts';
import { ref, computed, watch } from 'vue';
import { useAreaDataStore } from '@/stores/storageTracking/AreaDataStore.ts';
import { ICsvData } from '@/interfaces/storageTracking/ICsvData.ts';
import { exportCsv } from '@/utils/CsvExport.ts';
import { formatAllStatusesSmart } from '@/utils/storageTracking/ProjectStatus.ts';
import { IDecoratedDropdownItem } from '@/interfaces/general/IDecoratedDropdownItem.ts';
import { useUserAccessStore } from '@/stores/userAccessStore.ts';
import { useFilterDataStore } from '@/stores/storageTracking/FilterDataStore.ts';
import TitleBar from '@/components/layout/TitleBar.vue';
import { IMarker } from '@lcp/lcp-map';
import { useToast } from 'primevue/usetoast';
import BasicTable from '@/components/powerMarkets/tables/BasicTable.vue';
import { TRolesForSearch } from '@/interfaces/storageTracking/ILatestData.ts';
import { IBasicTableHeader } from '@/interfaces/general/basicTable/IBasicTableHeader.ts';
import { TBasicTableCellType } from '@/types/table/TBasicTableCellType.ts';
import { ICompanyReturn } from '@/interfaces/storageTracking/ICompanyReturn.ts';
import { IPublicProjectData } from '@/interfaces/storageTracking/IRedactedProjectData.ts';
import { useUIStore } from '@/stores/uiStore.ts';
import BaseStackedBarChart from '../charts/BaseStackedBarChart.vue';
import BaseHorizontalBarChart from '../charts/BaseHorizontalBarChart.vue';
import StorageTrackingSelector from '../inputs/StorageTrackingSelector.vue';
import ProjectPanel from '../projectSearch/ProjectPanel.vue';

const toast = useToast();
const uiStore = useUIStore();

interface ICsvDataRole extends ICsvData {
    role: string[];
}

const props = defineProps<{ selectedCompany: string; blocked?:boolean; lookAtAllDataProp?: boolean }>();

const allDataStore = useAllDataStore();
const areaDataStore = useAreaDataStore();
const filterDataStore = useFilterDataStore();
const userStore = useUserAccessStore();
const lookAtAllData = ref<boolean>(props.lookAtAllDataProp);

const loading = ref<boolean>(true);
const dataForCompanyPage = ref<ICompanyReturn | null>(null);

const filteredDataOptions = ref([{ name: 'Show only filtered data', value: false }, { name: 'Show all projects', value: true }]);

const AVAILABLE_ROLES = {
    developersSearch: 'Developer',
    ownersForSearch: 'Owner',
    operatorsForSearch: 'Operator',
    financersSearch: 'Financer',
    tradersForSearch: 'Trader',
    technologyProvidersForSearch: 'Technology Provider',
};

const BATTERY_TYPE_CHART_OPTIONS: Array<IDecoratedDropdownItem> = [
    { id: 'power', name: 'Power Capacity' },
    { id: 'storage', name: 'Storage Capacity' },
    { id: 'numberProjects', name: 'Number of projects' },
];

const CHART_SPLIT_OPTIONS: Array<IDecoratedDropdownItem> = [
    { id: 'year', name: 'Year (Operational)' },
    { id: 'technology', name: 'Technology' },
    { id: 'country', name: 'Country' },
    { id: 'status', name: 'Status' },
];

const projectsInvolvedTableHeaders: Array<IBasicTableHeader> = [
    { value: 'Project Name', type: 'text' },
    { value: 'Power Capacity', type: 'number', suffix: 'MW' },
    { value: 'Storage Capacity', type: 'number', suffix: 'MWh' },
    { value: 'Country', type: 'text' },
    { value: 'Roles', type: 'text' },
    { value: 'Technology', type: 'text' },
    { value: 'Status', type: 'text', noFilter: true },
];

const partnerTableHeaders: Array<IBasicTableHeader> = [
    { value: 'Partner', type: 'text' },
    { value: 'Role', type: 'text' },
    { value: 'Project Name', type: 'text' },
];

const filteredData = ref<Array<Record<string, TBasicTableCellType>>>([]);
const modalVisible = ref<boolean>(false);
const projectOpenInModal = ref<ICsvData | null>(null);
const companyOpenInModal = ref<string | null>(null);

const selectedBatteryType = ref<string>(BATTERY_TYPE_CHART_OPTIONS[0].id);
const selectedChartSplit = ref<string>(CHART_SPLIT_OPTIONS[0].id);

const isMobile = computed<boolean>(() => uiStore.isMobileView);

const selectedChartSplitName = computed<string>(() => CHART_SPLIT_OPTIONS.find(x => x.id === selectedChartSplit.value)?.name ?? selectedChartSplit.value);

const chartTooltipUnit = computed<string>(() => {
    switch (selectedBatteryType.value) {
        case 'power': return 'MW';
        case 'storage': return 'MWh';
        case 'numberProjects': return '';
        default: return '';
    }
});

const canExport = computed<boolean>(() => !userStore.assetTrackingIsTrialOrNoAccessOrMarketTrendsOnly);

const exportTooltip = computed<string>(() => (canExport.value ? 'Export as CSV' : 'CSV export is not available for this company.'));

const cleanChartTitle = computed<string>(() => {
    const dropdownItem = BATTERY_TYPE_CHART_OPTIONS.find(item => item.id === selectedBatteryType.value);
    return dropdownItem?.name || '';
});

const projectList = computed((): Array<ICsvDataRole> => {
    const filteredList: Array<ICsvDataRole> = [];
    if (!dataForCompanyPage.value) return filteredList;
    for (let i = 0; i < dataForCompanyPage.value.projects.length; i += 1) {
        if (
            dataForCompanyPage.value.projects[i].latestData.forecastYear === null
      && dataForCompanyPage.value.projects[i].latestData.latestDecommissionedYear === null
      && dataForCompanyPage.value.projects[i].latestData.latestCancelledOrAbandonedYear === null
        ) {
            const roleList: string[] = [];
            allDataStore.roles.forEach((role: TRolesForSearch) => {
                if (dataForCompanyPage.value!.projects[i].latestData[role]?.length > 0) {
                    dataForCompanyPage.value!.projects[i].latestData[role].forEach((j: string) => {
                        if (j === props.selectedCompany) {
                            roleList.push(AVAILABLE_ROLES[role]);
                        }
                    });
                }
            });

            if (roleList.length > 0) {
                const newRow = Object.assign(dataForCompanyPage.value.projects[i]);
                newRow.role = roleList;

                filteredList.push(newRow);
            }
        }
    }
    return filteredList;
});

const filteredProjectsInvolved = computed<Array<ICsvDataRole>>(() => projectList.value.filter((project: ICsvDataRole) => filteredData.value.some(value => value['Project Name'] === project.projectName)));

const filteredCountries = computed(() => filteredProjectsInvolved.value.map(x => x.country));

const projectsInvolvedTableData = computed<Array<Record<string, TBasicTableCellType>>>(() => {
    const dataRows: Array<Record<string, TBasicTableCellType>> = [];

    projectList.value.forEach((project: ICsvDataRole) => {
        dataRows.push({
            'Project Name': project.projectName,
            'Power Capacity': project.powerCapacityMw,
            'Storage Capacity': project.storageCapacityMwH,
            Country: areaDataStore.getCountryName(project.country),
            Roles: project.role,
            Technology: project.technology,
            Status: formatAllStatusesSmart(project),
        });
    });

    const lockIcon = { icon: 'lock', colour: 'var(--accentColour)' };
    dataForCompanyPage.value?.redactedProjects.forEach((project: IPublicProjectData) => {
        dataRows.push({
            'Project Name': project.projectName,
            'Power Capacity': lockIcon,
            'Storage Capacity': lockIcon,
            Country: areaDataStore.getCountryName(project.country),
            Roles: lockIcon,
            Technology: lockIcon,
            Status: ['You do not have access to this project'],
        });
    });

    return dataRows;
});

const partnerTableData = computed<Array<Record<string, string>>>(() => {
    const dataRows: Array<Record<string, string>> = [];

    projectList.value.forEach((project) => {
        allDataStore.roles.forEach((role) => {
            if (project.latestData[role]?.length === 0) return;
            project.latestData[role].forEach((company) => {
                if (company === props.selectedCompany) return;
                dataRows.push({
                    Partner: company,
                    Role: AVAILABLE_ROLES[role],
                    'Project Name': project.projectName,
                });
            });
        });
    });

    return dataRows;
});

function updateFilteredData (updatedData: Array<Record<string, TBasicTableCellType>>): void {
    filteredData.value = updatedData;
}

function createMarker (project: ICsvDataRole): IMarker {
    const marker: IMarker = {
        id: project.projectName,
        longitude: project.longitude as number,
        latitude: project.latitude as number,
        radius: 20,
        depth: 15,
        edgeCount: 20,
    };
    return marker;
}

const projectsInvolvedMarkers = computed((): Array<IMarker> => {
    const initialisedMarkerArray: Array<IMarker> = [];
    filteredProjectsInvolved.value.forEach((project) => {
        const tempMarker = createMarker(project);
        initialisedMarkerArray.push(tempMarker);
    });
    return initialisedMarkerArray;
});

function findRelevantAttributeOnProject (project: ICsvData): number {
    switch (selectedBatteryType.value) {
        case 'power':
            return project.powerCapacityMw || 0;
        case 'storage':
            return project.storageCapacityMwH || 0;
        case 'numberProjects':
            return 1;
        default:
            return 0;
    }
}

const countryTechOrStatusChartData = computed<Array<{ key: string; value: number }>>(() => {
    const data: Array<{ key: string; value: number }> = [];
    const dataRecord: Record<string, number> = {};

    switch (selectedChartSplit.value) {
        case 'country':
            projectList.value.forEach((project) => {
                const countryName = areaDataStore.getCountryName(project.country);
                if (!dataRecord[countryName]) dataRecord[countryName] = 0;
                dataRecord[countryName] += findRelevantAttributeOnProject(project);
            });

            Object.entries(dataRecord).forEach(([recordKey, recordValue]: [string, number]) => data.push(({ key: recordKey, value: recordValue })));
            break;
        case 'technology':
            projectList.value.forEach((project) => {
                project.technology.forEach((tech) => {
                    if (!dataRecord[tech]) dataRecord[tech] = 0;
                    dataRecord[tech] += findRelevantAttributeOnProject(project);
                });
            });

            Object.entries(dataRecord).forEach(([recordKey, recordValue]: [string, number]) => data.push(({ key: recordKey, value: recordValue })));
            break;
        case 'status':
            dataRecord.Operational = 0;
            dataRecord['In development'] = 0;
            projectList.value.forEach((row) => {
                if (row.isOperationalNamedProject) dataRecord.Operational += findRelevantAttributeOnProject(row);
                else dataRecord['In development'] += findRelevantAttributeOnProject(row);
            });
            Object.entries(dataRecord).forEach(([recordKey, recordValue]: [string, number]) => data.push(({ key: recordKey, value: recordValue })));
            break;
        default:
            break;
    }

    return data;
});

const yearChartData = computed<Record<number, Record<string, number>>>(() => {
    const yearsList: Record<number, Record<string, number>> = {};
    allDataStore.years.forEach((year) => {
        yearsList[year] = {};
        projectList.value.forEach((project) => {
            if (project.latestData.operationalYear !== year) return;
            const countryName = areaDataStore.getCountryName(project.country);
            if (!yearsList[year][countryName]) yearsList[year][countryName] = 0;
            yearsList[year][countryName] += findRelevantAttributeOnProject(project);
        });
    });
    return yearsList;
});

function exportCompanyCsv (): void {
    if (!canExport.value) return;

    const data: Array<Array<string | number | undefined>> = [];

    const projectTitleRow = ['Projects involed in', 'Power Capacity (MW)', 'Storage Capacity (MWh)', 'Segment', 'Installation', 'Country', 'Role', 'Technology', 'Status'];

    data.push(projectTitleRow);

    projectList.value.forEach(project => {
        const projectRow = [project.projectName, project.powerCapacityMw ?? '', project.storageCapacityMwH ?? '', project.customerSegment, project.installation,
            areaDataStore.getCountryName(project.country), project.role.join(', '), project.technology.join(', '), formatAllStatusesSmart(project).join(', ')];
        data.push(projectRow);
    });

    data.push([]);

    const partnerTitleRow = ['Partner', 'Role', 'Project'];

    data.push(partnerTitleRow);

    partnerTableData.value.forEach((dataRow) => {
        const partnerDataRow = [
            dataRow.Partner.concat(' ,'),
            dataRow.Role.concat(' ,'),
            dataRow['Project Name'].concat(' ,'),
        ];
        data.push(partnerDataRow);
    });

    exportCsv(data, props.selectedCompany);
}

function openProjectDialog (row: Record<string, TBasicTableCellType>): void {
    projectOpenInModal.value = dataForCompanyPage.value?.projects.find(project => project.projectName === row['Project Name']) ?? null;
    modalVisible.value = true;
}

async function openPartnersDialog (value: TBasicTableCellType, header: string): Promise<void> {
    if (typeof value !== 'string') return;
    switch (header) {
        case 'Partner': {
            const userHasAccessToCompany = allDataStore.trialUserCompanyLimits ? allDataStore.trialUserCompanyLimits[value] : null;
            if (userHasAccessToCompany) {
                companyOpenInModal.value = value;
                modalVisible.value = true;
                break;
            }
            toast.add({ severity: 'warn', summary: 'You do not have access to this company', life: 1500 });
            break;
        }
        case 'Project Name': {
            const projectInfo = projectList.value.find(project => project.projectName === value) ?? null;
            if (!projectInfo) break;
            projectOpenInModal.value = projectInfo;
            modalVisible.value = true;
            break;
        }
        default:
            break;
    }
}

watch(() => props.selectedCompany, (newValue) => {
    if (newValue) {
        initialise();
    }
});

async function initialise (): Promise<void> {
    loading.value = true;
    dataForCompanyPage.value = await DataApi.getCompanyData(props.selectedCompany, lookAtAllData.value ? filterDataStore.neutralFilters : filterDataStore.filters);
    loading.value = false;
}

initialise();

watch(() => modalVisible.value, newState => {
    if (newState) return;
    projectOpenInModal.value = null;
    companyOpenInModal.value = null;
});

</script>

<style lang="scss" scoped>
.company-panel-wrapper {
    display: grid;
    grid-template-rows: auto 1fr;
    height: 100%;
    background-color: var(--neutral100);
    .left {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 10px;
    }
    .company-panel-inner {
        display: grid;
        gap: var(--defaultSpacing);
        padding: var(--defaultSpacing);
        overflow-y: auto;

        .company-map-container {
            min-height: 500px;

            .map-title {
                padding: var(--defaultSpacing) 0;
                background-color: var(--neutral300);
            }
        }

        .chart {
            min-height: 400px;
        }
    }
}

.partner-table, .projects-table {
    max-height: 600px;
}

</style>
